import { Theme } from '@mui/material/styles';
import { tableCellClasses } from '@mui/material/TableCell';
import { tableRowClasses } from '@mui/material/TableRow';

import { varAlpha } from '../styles/index.js';

// ----------------------------------------------------------------------

const MuiTableContainer = {
  /** **************************************
   * STYLE
   *************************************** */
  styleOverrides: {
    root: ({ theme }: { theme: Theme }) => ({
      position: 'relative',
      scrollbarWidth: 'thin',
      scrollbarColor: `${varAlpha((theme.palette.text as any).disabledChannel, 0.4)} ${varAlpha(
        (theme.palette.text as any).disabledChannel,
        0.08
      )}`,
    }),
  },
};

// ----------------------------------------------------------------------

const MuiTable = {
  /** **************************************
   * STYLE
   *************************************** */
  styleOverrides: {
    root: ({ theme }: { theme: Theme }) => ({
      '--palette-TableCell-border': theme.palette.divider,
    }),
  },
};

// ----------------------------------------------------------------------

const MuiTableRow = {
  /** **************************************
   * STYLE
   *************************************** */
  styleOverrides: {
    root: ({ theme }: { theme: Theme }) => ({
      [`&.${tableRowClasses.selected}`]: {
        backgroundColor: varAlpha((theme.palette.primary as any).darkChannel, 0.04),
        '&:hover': { backgroundColor: varAlpha((theme.palette.primary as any).darkChannel, 0.08) },
      },
      '&:last-of-type': { [`& .${tableCellClasses.root}`]: { borderColor: 'transparent' } },
    }),
  },
};

// ----------------------------------------------------------------------

const MuiTableCell = {
  /** **************************************
   * STYLE
   *************************************** */
  styleOverrides: {
    root: { borderBottomStyle: 'dashed' },
    head: ({ theme }: { theme: Theme }) => ({
      fontSize: 14,
      color: theme.palette.text.secondary,
      fontWeight: (theme.typography as any).fontWeightSemiBold,
      backgroundColor: (theme.palette.background as any).neutral,
    }),
    stickyHeader: ({ theme }: { theme: Theme }) => ({
      backgroundColor: (theme.palette.background as any).paper,
      backgroundImage: `linear-gradient(to bottom, ${
        (theme.palette.background as any).neutral
      } 0%, ${(theme.palette.background as any).neutral} 100%)`,
    }),
    paddingCheckbox: ({ theme }: { theme: Theme }) => ({ paddingLeft: theme.spacing(1) }),
  },
};

// ----------------------------------------------------------------------

const MuiTablePagination = {
  /** **************************************
   * DEFAULT PROPS
   *************************************** */
  defaultProps: {
    backIconButtonProps: { size: 'small' },
    nextIconButtonProps: { size: 'small' },
    slotProps: { select: { name: 'table-pagination-select' } },
  },

  /** **************************************
   * STYLE
   *************************************** */
  styleOverrides: {
    root: { width: '100%' },
    toolbar: { height: 64 },
    // Both labels render as `<p>`, and Bootstrap's reboot (loaded by the Metronic
    // stylesheet) gives every paragraph a 1rem bottom margin. Inside the toolbar's
    // centered flex row that taller box lifts the text off the line the select and
    // the page arrows sit on, so the row reads as misaligned.
    selectLabel: { marginBottom: 0, marginTop: 0 },
    displayedRows: { marginBottom: 0, marginTop: 0 },
    actions: { marginRight: 8 },
    select: ({ theme }: { theme: Theme }) => ({
      paddingLeft: 8,
      '&:focus': { borderRadius: theme.shape.borderRadius },
    }),
    selectIcon: {
      right: 4,
      width: 16,
      height: 16,
      top: 'calc(50% - 8px)',
    },
  },
};

// ----------------------------------------------------------------------

export const table = {
  MuiTable,
  MuiTableRow,
  MuiTableCell,
  MuiTableContainer,
  MuiTablePagination,
};
